/**
 * useSprintPlanning — the React binding for the Sprint Planning surface: one
 * sprint, its committed tasks, the room's backlog, the linked team's members,
 * and the computed capacity picture.
 *
 * Strict layering: the page calls THIS hook; the hook calls {@link SprintStore},
 * {@link TaskStore}, and {@link TeamStore}; the stores call the api facade. The
 * page never imports a store or the api directly.
 *
 * Data model: a sprint's board and the room's backlog are two projections of
 * the SAME room-scoped task list (backlog = tasks with no sprintId; sprint =
 * tasks whose sprintId is this sprint), so ONE room-scoped query feeds both
 * panes. Assigning/unassigning a task just flips its sprintId — done
 * optimistically here, then persisted and re-fetched.
 *
 * Capacity is derived purely (no I/O) from the linked team's members × the
 * sprint's working days vs the sprint's committed effort, via the Phase-1
 * capacity engine.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { SprintStore } from './stores/sprintStore';
import { TaskStore } from '../tasks/taskStore';
import { TeamStore } from '../teams/teamStore';
import {
  commonReductionDays as sumCommonReductionDays,
  commonTaskId,
  memberOncallDays,
  sprintWorkingDays,
} from './models/sprintboardModel';
import {
  capacitySummary,
  type CapacityMember,
  type CapacitySummary,
} from './engine/capacity';
import { isSubtask, totalEffortDays } from '../tasks/taskModel';
import type { CommonTask, Sprint } from './models/types';
import type { Task } from '../tasks/types';
import type { TeamMember } from '../teams/types';

export interface UseSprintPlanningResult {
  /** The sprint being planned, or null when not loaded / not found. */
  sprint: Sprint | null;
  /** The room's backlog: non-deleted room tasks with no sprint assignment. */
  backlog: Task[];
  /** Tasks committed to this sprint. */
  sprintTasks: Task[];
  /** Members of the sprint's linked team (empty when no team is linked). */
  members: TeamMember[];
  /** Working days (Mon–Fri) in the sprint's date range. */
  workingDays: number;
  /** Full capacity summary (team/committed/remaining + per-engineer loads). */
  capacity: CapacitySummary;
  /** The sprint's common / all-hands tasks (each reduces every member's bandwidth). */
  commonTasks: CommonTask[];
  /** Manually-entered on-call days per member alias for this sprint. */
  oncallDaysByAlias: Record<string, number>;
  /** Total per-member bandwidth reduction from the common tasks (ideal days). */
  commonReductionDays: number;
  loading: boolean;
  error: string | null;
  /** True while an assign/unassign write is in flight. */
  saving: boolean;
  refresh: () => Promise<void>;
  /** Assigns a task to this sprint, or returns it to the backlog with `null`. */
  assign: (task: Task, sprintId: string | null) => Promise<void>;
  /** Sets a member's on-call days for this sprint (clamped to [0, workingDays]). */
  setOncallDays: (alias: string, days: number) => Promise<void>;
  /** Adds a common / all-hands task to this sprint. */
  addCommonTask: (name: string, daysPerMember: number) => Promise<void>;
  /** Removes a common / all-hands task from this sprint by id. */
  removeCommonTask: (id: string) => Promise<void>;
}

export function useSprintPlanning(
  sprintId: string | undefined,
): UseSprintPlanningResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      sprints: new SprintStore(api),
      tasks: new TaskStore(api),
      teams: new TeamStore(api),
    }),
    [api],
  );

  const [sprint, setSprint] = useState<Sprint | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!sprintId) {
      setSprint(null);
      setTasks([]);
      setMembers([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const loaded = await stores.sprints.get(sprintId);
      if (!loaded || loaded.deleted) {
        setSprint(null);
        setTasks([]);
        setMembers([]);
        setError('Sprint not found.');
        return;
      }
      setSprint(loaded);
      const [roomTasks, team] = await Promise.all([
        stores.tasks.listByRoom(loaded.roomId),
        loaded.teamId
          ? stores.teams.get(loaded.teamId)
          : Promise.resolve(undefined),
      ]);
      setTasks(roomTasks);
      setMembers(team && !team.deleted ? team.members ?? [] : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sprint.');
    } finally {
      setLoading(false);
    }
  }, [sprintId, stores]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const assign = useCallback(
    async (task: Task, targetSprintId: string | null) => {
      // Optimistic: flip the task's sprintId locally so it jumps panes at once.
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id ? { ...t, sprintId: targetSprintId ?? null } : t,
        ),
      );
      setSaving(true);
      try {
        await stores.tasks.assignToSprint(task, targetSprintId);
        await refresh();
      } catch (e) {
        await refresh(); // revert to server truth on failure
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [stores, refresh],
  );

  // Planning shows only TOP-LEVEL work; subtasks live under their parent and
  // never get sprint-assigned independently or counted toward capacity.
  const backlog = useMemo(
    () => tasks.filter((t) => !t.sprintId && !isSubtask(t)),
    [tasks],
  );
  const sprintTasks = useMemo(
    () =>
      sprint
        ? tasks.filter((t) => t.sprintId === sprint.id && !isSubtask(t))
        : [],
    [tasks, sprint],
  );

  const workingDays = useMemo(
    () => (sprint ? sprintWorkingDays(sprint.startDate, sprint.endDate) : 0),
    [sprint],
  );

  // Subtasks keyed by parent id (from the full room task list), so a top-level
  // task that was never estimated directly still counts its subtasks' effort.
  const subtasksByParent = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const t of tasks) {
      if (!isSubtask(t)) continue;
      const parentId = (t.parentTaskId ?? '').trim();
      if (!parentId) continue;
      const arr = map.get(parentId);
      if (arr) arr.push(t);
      else map.set(parentId, [t]);
    }
    return map;
  }, [tasks]);

  const commonTasks = useMemo<CommonTask[]>(
    () => sprint?.commonTasks ?? [],
    [sprint],
  );
  const oncallDaysByAlias = useMemo<Record<string, number>>(
    () => sprint?.oncallDaysByAlias ?? {},
    [sprint],
  );
  const commonReductionDays = useMemo(
    () => sumCommonReductionDays(commonTasks),
    [commonTasks],
  );

  const capacity = useMemo(() => {
    const capacityMembers: CapacityMember[] = members.map((m) => ({
      alias: m.alias,
      oncallDays: memberOncallDays(oncallDaysByAlias, m.alias, workingDays),
    }));
    // Committed effort uses each task's TOTAL effort (its own custom effort, or
    // the sum of its subtasks) so subtask-only tasks count toward capacity.
    const effectiveTasks = sprintTasks.map((t) => ({
      owner: t.owner,
      effortDays: totalEffortDays(t, subtasksByParent.get(t.id)),
    }));
    return capacitySummary(
      capacityMembers,
      workingDays,
      effectiveTasks,
      commonReductionDays,
    );
  }, [
    members,
    workingDays,
    sprintTasks,
    subtasksByParent,
    oncallDaysByAlias,
    commonReductionDays,
  ]);

  const setOncallDays = useCallback(
    async (alias: string, days: number) => {
      if (!sprint) return;
      const next: Record<string, number> = { ...(sprint.oncallDaysByAlias ?? {}) };
      const value = Number.isFinite(days) && days > 0 ? days : 0;
      if (value > 0) next[alias] = value;
      else delete next[alias];
      setSprint((prev) => (prev ? { ...prev, oncallDaysByAlias: next } : prev));
      setSaving(true);
      try {
        await stores.sprints.setBandwidth(sprint, { oncallDaysByAlias: next });
        await refresh();
      } catch (e) {
        await refresh();
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [sprint, stores, refresh],
  );

  const addCommonTask = useCallback(
    async (name: string, daysPerMember: number) => {
      if (!sprint) return;
      const trimmed = name.trim();
      if (!trimmed) return;
      const value =
        Number.isFinite(daysPerMember) && daysPerMember > 0 ? daysPerMember : 0;
      const next: CommonTask[] = [
        ...(sprint.commonTasks ?? []),
        { id: commonTaskId(), name: trimmed, daysPerMember: value },
      ];
      setSprint((prev) => (prev ? { ...prev, commonTasks: next } : prev));
      setSaving(true);
      try {
        await stores.sprints.setBandwidth(sprint, { commonTasks: next });
        await refresh();
      } catch (e) {
        await refresh();
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [sprint, stores, refresh],
  );

  const removeCommonTask = useCallback(
    async (id: string) => {
      if (!sprint) return;
      const next = (sprint.commonTasks ?? []).filter((t) => t.id !== id);
      setSprint((prev) => (prev ? { ...prev, commonTasks: next } : prev));
      setSaving(true);
      try {
        await stores.sprints.setBandwidth(sprint, { commonTasks: next });
        await refresh();
      } catch (e) {
        await refresh();
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [sprint, stores, refresh],
  );

  return {
    sprint,
    backlog,
    sprintTasks,
    members,
    workingDays,
    capacity,
    commonTasks,
    oncallDaysByAlias,
    commonReductionDays,
    loading,
    error,
    saving,
    refresh,
    assign,
    setOncallDays,
    addCommonTask,
    removeCommonTask,
  };
}
